import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { obligationsApi } from "../../api/obligationsApi";
import { toast } from "../../components/common/toast";
import type { AppDispatch, RootState } from "../../app/store";
import { getCurrentUser } from "../../auth/auth.service";
import { getApiErrorMessage } from "../../utils/apiError";
import i18n from "../../i18n";
import axios from "axios";
import { uploadMediaFiles, createResolvedMedia } from "../../utils/mediaUtils";
import { api } from "../../api/axiosClient";

export interface Obligation {
  id?: string;

  clNo?: string;
  title?: string;
  desc?: string;

  actFrequency?: any;
  recFrequency?: any;

  startDate?: string;
  endDate?: string;

  department?: any;
  departmentName?: string;

  reminderTime?: any;
  remarks?: string;

  businessUnit?: any;
  businessUnitName?: string;

  mapRegion?: any;
  local?: any;

  connectedAgreement?: any;
  connectedParty?: any;

  extraCol1?: any;
  dependedOb?: any;

  createdBy?: string;
  createdId?: string;
  createdByEmail?: string;

  closed?: boolean;

  createdAt?: string;
  updatedAt?: string;

  /** Connected obligations (see features/obligations/obligationDependency). */
  dependencyStatus?: number;
  isConnected?: boolean;
  predecessorIds?: string[];
  predecessorLabels?: string[];
  dependentIds?: string[];
  plannedStartDate?: string | null;
  suggestedStartDate?: string | null;
  effectiveStartDate?: string | null;
  dependencyException?: string | null;
  blockedReason?: string | null;

  [key: string]: any;
}

export interface ObligationsSearchPayload {
  companyId?: string;
  roleId?: string;
  isAppAdmin?: boolean;
  pageSize?: number;
  pageIndex?: number;
  orderBy?: string;
  sortAscending?: boolean;
  includeFacets?: boolean;
  groupIds?: string[];
  includeDocument?: boolean;
  ids?: string[];
  isActive?: boolean | null;
  createdBy?: string[];
  updatedBy?: string[];
  currentProfileId?: string;
  startDate?: string;
  endDate?: string;
  myMode?: boolean;
  searchText?: string;
  status?: string;
  /** Contract-level filters used by the Obligations Hub search endpoint. */
  contractCategoryIds?: string[];
  /** Party filter: the contract's other party, by name. */
  partyNames?: string[];
  /** Responsible-person filter: the members the obligation is assigned to. */
  assigneeIds?: string[];
  contractTitleIds?: string[];
  businessUnitIds?: string[];
  includeDeleted?: boolean;
  excludeOccurrences?: boolean;
  excludeSeries?: boolean;
  obligationStatuses?: number[];
  complianceStatuses?: number[];
  /** Connected obligations: only those in a chain, or only independent ones. */
  connection?: "connected" | "independent";
  /** DependencyStatus values (features/obligations/obligationDependency). */
  dependencyStatuses?: number[];
  dependencyExceptionOnly?: boolean;
  predecessorIds?: string[];
  dependentIds?: string[];
}

interface ObligationsState {
  items: Obligation[];

  selectedObligation: Obligation | null;

  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;

  error: string | null;

  pageIndex: number;
  hasMore: boolean;
  totalObligations: number;

  obligationContracts: any[];
  isObligationContractLoading: boolean;
  isCreatingObligationContract: boolean;
}

const initialState: ObligationsState = {
  items: [],

  selectedObligation: null,

  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,

  error: null,

  pageIndex: 0,
  hasMore: true,
  totalObligations: 0,

  obligationContracts: [],
  isObligationContractLoading: false,
  isCreatingObligationContract: false,
};

const getErrorMessage = (error: any, fallback: string) => {
  try {
    return (
      getApiErrorMessage(error, fallback) ||
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      fallback
    );
  } catch {
    return fallback;
  }
};

const getResponseData = (response: any) => {
  return response?.data ?? response;
};

const obligationMediaApi = {
  initUpload: async (ids: string[], groupId?: string) => {
    const response = await api.post("/obligation/initupload", { ids, groupId });
    return response.data;
  },

  uploadToS3: async (s3Url: string, file: File) => {
    return await axios.put(s3Url, file, {
      headers: { "Content-Type": file.type },
    });
  },

  uploadMedia: async (_entityId: string, medias: any[]) => {
    return await api.post("/obligation/uploadmedia", { medias });
  },

  getMediaLink: async (ids: string[], size: number = 3, groupId?: string) => {
    const response = await api.post("/obligation/getmedialink", {
      ids,
      size,
      groupId,
    });
    return response.data;
  },
};

const MEDIA_TYPE_ATTACHMENT = 3;

/** Resolve obligation media metadata to signed URLs using the same flow as Contracts. */
export const resolveObligationMediaLinks = (
  medias: any[] = [],
  groupId?: string,
) => {
  return async () => {
    const source = Array.isArray(medias) ? medias.slice(0, 1) : [];
    if (!source.length) return [];

    const ids = source
      .map((media: any) => media?.s3Url || media?.id)
      .filter(Boolean)
      .map(String);

    if (!ids.length) return source;

    try {
      const response = await obligationMediaApi.getMediaLink(
        ids,
        MEDIA_TYPE_ATTACHMENT,
        groupId,
      );

      const links = Array.isArray(response?.links) ? response.links : [];
      const linkById = new Map<string, string>();
      links.forEach((item: any) => {
        if (item?.id && item?.link) linkById.set(String(item.id), item.link);
      });

      return source.map((media: any) => {
        const key = String(media?.s3Url || media?.id || "");
        return {
          ...media,
          url:
            media?.url ||
            media?.fileUrl ||
            media?.mediaUrl ||
            linkById.get(key) ||
            "",
        };
      });
    } catch (error) {
      console.error("Obligation media resolution failed:", error);
      return source;
    }
  };
};

export const uploadObligationMedia = (
  entityId: string,
  files: File[],
  groupId?: string,
) => {
  return async () => {
    if (!files?.length) return [];

    const { payloads, links } = await uploadMediaFiles(
      obligationMediaApi,
      entityId,
      files,
      groupId,
    );

    const userId = (await getCurrentUser()).userId;
    const resolvedMedia = createResolvedMedia(
      payloads,
      links,
      entityId,
      userId,
    );

    const uploadedMedia = resolvedMedia?.length
      ? resolvedMedia
      : payloads.map((entry: any) => entry?.payload).filter(Boolean);

    const ids = uploadedMedia
      .map((media: any) => media?.s3Url || media?.id)
      .filter(Boolean)
      .map(String);

    if (!ids.length) return uploadedMedia;

    try {
      const response = await obligationMediaApi.getMediaLink(
        ids,
        MEDIA_TYPE_ATTACHMENT,
        groupId,
      );
      const linkById = new Map<string, string>();
      for (const link of response?.links || []) {
        if (link?.id && link?.link) linkById.set(String(link.id), link.link);
      }

      return uploadedMedia.map((media: any) => ({
        ...media,
        url:
          media?.url ||
          linkById.get(String(media?.s3Url || media?.id || "")) ||
          "",
      }));
    } catch (error) {
      console.error("Uploaded obligation media link resolution failed:", error);
      return uploadedMedia;
    }
  };
};

// The newest first-page request. A fresh search term must not be dropped
// because an earlier one is still loading, so a first page always runs and an
// answer to anything older than it is discarded.
let latestFirstPage = 0;

export const fetchObligations = (payload: ObligationsSearchPayload = {}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().obligations;
    const incomingPageIndex = payload.pageIndex ?? state.pageIndex;

    const isFirstPage = incomingPageIndex === 0;
    if ((state.isLoading || !state.hasMore) && !isFirstPage) {
      return;
    }
    const ticket = isFirstPage ? ++latestFirstPage : latestFirstPage;
    const superseded = () => ticket !== latestFirstPage;

    dispatch(setObligationLoading(true));
    dispatch(clearObligationError());

    try {
      const currentUser = await getCurrentUser();

      const requestBody = {
        companyId: payload.companyId ?? currentUser?.companyId,
        isAppAdmin: payload.isAppAdmin ?? currentUser?.isAppAdmin ?? false,
        pageSize: payload.pageSize ?? 10,
        pageIndex: incomingPageIndex,
        orderBy: payload.orderBy ?? "",
        sortAscending: payload.sortAscending ?? false,
        includeFacets: payload.includeFacets ?? false,
        groupIds: payload.groupIds ?? [],
        // Off unless asked for, as with contracts: the body is ~99% of a row.
        includeDocument: payload.includeDocument ?? false,
        ids: payload.ids ?? [],
        isActive: payload.isActive ?? null,
        createdBy: payload.createdBy ?? [],
        updatedBy: payload.updatedBy ?? [],
        currentProfileId:
          payload.currentProfileId ?? currentUser?.id ?? currentUser?.userId,
        ...(payload.startDate
          ? {
              startDate: payload.startDate,
            }
          : {}),
        ...(payload.endDate
          ? {
              endDate: payload.endDate,
            }
          : {}),
        myMode: payload.myMode ?? true,
        searchText: payload.searchText ?? "",
        contractCategoryIds: payload.contractCategoryIds ?? [],
        partyNames: payload.partyNames ?? [],
        ...(payload.assigneeIds?.length
          ? { assigneeIds: payload.assigneeIds }
          : {}),
        contractTitleIds: payload.contractTitleIds ?? [],
        businessUnitIds: payload.businessUnitIds ?? [],
        includeDeleted: payload.includeDeleted ?? false,
        // A register lists each recurring obligation once, not every period of it.
        excludeOccurrences: payload.excludeOccurrences ?? false,
        excludeSeries: payload.excludeSeries ?? false,
        obligationStatuses: payload.obligationStatuses ?? [],
        complianceStatuses: payload.complianceStatuses ?? [],
        // Connected-obligation filters, sent only when set.
        ...(payload.connection ? { connection: payload.connection } : {}),
        ...(payload.dependencyStatuses?.length
          ? { dependencyStatuses: payload.dependencyStatuses }
          : {}),
        ...(payload.dependencyExceptionOnly
          ? { dependencyExceptionOnly: true }
          : {}),
        ...(payload.predecessorIds?.length
          ? { predecessorIds: payload.predecessorIds }
          : {}),
        ...(payload.dependentIds?.length
          ? { dependentIds: payload.dependentIds }
          : {}),
      };

      const response = await obligationsApi.searchObligations(requestBody);
      if (superseded()) return;
      const data = getResponseData(response);

      const obligations =
        data?.data ||
        data?.items ||
        data?.results ||
        (Array.isArray(data) ? data : []);

      const totalCount =
        response?.count ??
        response?.totalCount ??
        data?.count ??
        data?.totalCount ??
        0;

      const currentLoadedCount =
        incomingPageIndex === 0
          ? obligations.length
          : state.items.length + obligations.length;

      const pageSize = payload.pageSize ?? 10;

      dispatch(setTotalObligations(Number(totalCount) || 0));

      dispatch(
        setHasMore(
          currentLoadedCount < Number(totalCount || 0) &&
            obligations.length >= pageSize,
        ),
      );

      if (incomingPageIndex === 0) {
        dispatch(setObligations(obligations));
        dispatch(setObligationPageIndex(1));
      } else {
        dispatch(appendObligations(obligations));
        dispatch(setObligationPageIndex(incomingPageIndex + 1));
      }

      return response;
    } catch (error: any) {
      const message = getApiErrorMessage(
        error,
        i18n.t("obligations:sliceMessages.fetchError", {
          defaultValue: "Failed to fetch obligations",
        }),
      );

      dispatch(setObligationError(message));

      throw error;
    } finally {
      if (!superseded()) dispatch(setObligationLoading(false));
    }
  };
};

export const fetchObligationById = (id: string) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setObligationLoading(true));
    dispatch(clearObligationError());

    try {
      const response = await obligationsApi.searchObligations({
        pageIndex: 0,
        pageSize: 1,
        orderBy: "",
        sortAscending: true,
        sortDecending: false,
        includeFacets: false,
        // One row, for the editor: the body is needed here.
        includeDocument: true,
        ids: [id],
      });

      const data = getResponseData(response);

      const obligations =
        data?.data ||
        data?.items ||
        data?.results ||
        (Array.isArray(data) ? data : []);

      const obligation = obligations?.[0] || null;

      dispatch(setSelectedObligation(obligation));

      return obligation;
    } catch (error: any) {
      console.error("Fetch Obligation By Id Error:", error);

      const message = getErrorMessage(
        error,
        i18n.t("obligations:sliceMessages.fetchByIdError", {
          defaultValue: "Failed to fetch obligation",
        }),
      );

      dispatch(setObligationError(message));

      toast.error(
        i18n.t("obligations:sliceMessages.fetchByIdError", {
          defaultValue: "Failed to fetch obligation",
        }),
      );

      throw error;
    } finally {
      dispatch(setObligationLoading(false));
    }
  };
};

export const fetchObligationsForAgreement = ({
  agreementId,
  local,
}: {
  agreementId: string;
  local?: boolean;
}) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setObligationLoading(true));
    dispatch(clearObligationError());

    try {
      const response = await obligationsApi.searchObligations({
        pageIndex: 0,
        pageSize: 100,
        orderBy: "",
        sortAscending: true,
        sortDecending: false,
        includeFacets: false,
        includeDocument: false,

        connectedAgreement: agreementId,

        ...(local !== undefined ? { local } : {}),
      });

      const data = getResponseData(response);

      const obligations =
        data?.data ||
        data?.items ||
        data?.results ||
        (Array.isArray(data) ? data : []);

      dispatch(setObligations(obligations));

      return response;
    } catch (error: any) {
      console.error("Fetch Obligations For Agreement Error:", error);

      const message = getErrorMessage(
        error,
        i18n.t("obligations:sliceMessages.fetchAgreementError", {
          defaultValue: "Failed to fetch agreement obligations",
        }),
      );

      dispatch(setObligationError(message));

      toast.error(
        i18n.t("obligations:sliceMessages.fetchAgreementError", {
          defaultValue: "Failed to fetch agreement obligations",
        }),
      );

      throw error;
    } finally {
      dispatch(setObligationLoading(false));
    }
  };
};

export const createObligation = (formData: any) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setCreatingObligation(true));
    dispatch(clearObligationError());

    try {
      const response = await obligationsApi.createObligation(formData);

      const createdObligation =
        getResponseData(response)?.data ||
        getResponseData(response) ||
        formData;

      dispatch(addObligationToList(createdObligation));

      toast.success(
        i18n.t("obligations:sliceMessages.createSuccess", {
          defaultValue: "Obligation created successfully!",
        }),
      );

      return createdObligation;
    } catch (error: any) {
      console.error("Create Obligation Error:", error);

      const message = getErrorMessage(
        error,
        i18n.t("obligations:sliceMessages.createError", {
          defaultValue: "Failed to create obligation",
        }),
      );

      dispatch(setObligationError(message));

      toast.error(
        getErrorMessage(
          error,
          i18n.t("obligations:sliceMessages.createError", {
            defaultValue: "Failed to create obligation",
          }),
        ),
      );

      throw error;
    } finally {
      dispatch(setCreatingObligation(false));
    }
  };
};

export const updateObligation = (formData: any) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setUpdatingObligation(true));
    dispatch(clearObligationError());

    try {
      const response = await obligationsApi.updateObligation(formData);

      const updatedObligation =
        getResponseData(response)?.data ||
        getResponseData(response) ||
        formData;

      dispatch(updateObligationInList(updatedObligation));

      if (
        updatedObligation?.id &&
        updatedObligation.id === updatedObligation?.id
      ) {
        dispatch(setSelectedObligation(updatedObligation));
      }

      toast.success(
        i18n.t("obligations:sliceMessages.updateSuccess", {
          defaultValue: "Obligation updated successfully!",
        }),
      );

      return updatedObligation;
    } catch (error: any) {
      console.error("Update Obligation Error:", error);

      const message = getErrorMessage(
        error,
        i18n.t("obligations:sliceMessages.updateError", {
          defaultValue: "Failed to update obligation",
        }),
      );

      dispatch(setObligationError(message));

      toast.error(
        getErrorMessage(
          error,
          i18n.t("obligations:sliceMessages.updateError", {
            defaultValue: "Failed to update obligation",
          }),
        ),
      );

      throw error;
    } finally {
      dispatch(setUpdatingObligation(false));
    }
  };
};

export const deleteObligation = (id: string, reason: string) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setDeletingObligation(true));
    dispatch(clearObligationError());

    try {
      const response = await obligationsApi.removeObligation(id, reason);

      dispatch(removeObligationFromList(id));

      const selected = undefined;

      void selected;

      toast.success(
        i18n.t("obligations:sliceMessages.deleteSuccess", {
          defaultValue: "Obligation removed successfully!",
        }),
      );

      return response;
    } catch (error: any) {
      console.error("Delete Obligation Error:", error);

      const message = getErrorMessage(
        error,
        i18n.t("obligations:sliceMessages.deleteError", {
          defaultValue: "Failed to remove obligation",
        }),
      );

      dispatch(setObligationError(message));

      toast.error(
        getErrorMessage(
          error,
          i18n.t("obligations:sliceMessages.deleteError", {
            defaultValue: "Failed to remove obligation",
          }),
        ),
      );

      throw error;
    } finally {
      dispatch(setDeletingObligation(false));
    }
  };
};

export const restoreObligation = (id: string) => {
  return async (dispatch: AppDispatch) => {
    dispatch(clearObligationError());

    try {
      const response = await obligationsApi.restoreObligation(id);

      // Out of the deleted state straight away; the read model catches up shortly after.
      dispatch(
        updateObligationInList({
          id,
          deletedBy: null,
          deletedByName: null,
          deletionDate: null,
          deletionReason: null,
        } as unknown as Obligation),
      );

      toast.success(
        i18n.t("obligations:sliceMessages.restoreSuccess", {
          defaultValue: "Obligation restored successfully!",
        }),
      );

      void refreshRestoredObligation(id, dispatch);

      return response;
    } catch (error: any) {
      console.error("Restore Obligation Error:", error);

      const message = getErrorMessage(
        error,
        i18n.t("obligations:sliceMessages.restoreError", {
          defaultValue: "Failed to restore obligation",
        }),
      );

      dispatch(setObligationError(message));

      toast.error(
        getErrorMessage(
          error,
          i18n.t("obligations:sliceMessages.restoreError", {
            defaultValue: "Failed to restore obligation",
          }),
        ),
      );

      throw error;
    }
  };
};

/** Whether it comes back active is decided by the server, so the row is re-read once the search index has it. */
async function refreshRestoredObligation(id: string, dispatch: AppDispatch) {
  for (let attempt = 0; attempt < 5; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, 1000));

    try {
      const response = await obligationsApi.searchObligations({
        pageIndex: 0,
        pageSize: 1,
        orderBy: "",
        sortAscending: true,
        includeFacets: false,
        includeDocument: false,
        ids: [id],
        includeDeleted: true,
      });

      const rows = obligationsApi.rowsOf(getResponseData(response));
      const row = rows.find((r: any) => r?.id === id);

      if (row && !row.deletedBy && !row.deletionDate) {
        dispatch(updateObligationInList(row));
        return;
      }
    } catch {
      return;
    }
  }
}

export const activateObligation = (id: string) => {
  return async (dispatch: AppDispatch) => {
    dispatch(clearObligationError());

    try {
      const response = await obligationsApi.activateObligation(id);

      dispatch(
        updateObligationStatus({
          id,
          isActive: true,
        }),
      );

      toast.success(
        i18n.t("obligations:sliceMessages.activateSuccess", {
          defaultValue: "Obligation activated successfully!",
        }),
      );

      return response;
    } catch (error: any) {
      console.error("Activate Obligation Error:", error);

      const message = getErrorMessage(
        error,
        i18n.t("obligations:sliceMessages.activateError", {
          defaultValue: "Failed to activate obligation",
        }),
      );

      dispatch(setObligationError(message));

      toast.error(
        getErrorMessage(
          error,
          i18n.t("obligations:sliceMessages.activateError", {
            defaultValue: "Failed to activate obligation",
          }),
        ),
      );

      throw error;
    }
  };
};

export const deActivateObligation = (id: string) => {
  return async (dispatch: AppDispatch) => {
    dispatch(clearObligationError());

    try {
      const response = await obligationsApi.deActivateObligation(id);

      dispatch(
        updateObligationStatus({
          id,
          isActive: false,
        }),
      );

      toast.success(
        i18n.t("obligations:sliceMessages.deactivateSuccess", {
          defaultValue: "Obligation deactivated successfully!",
        }),
      );

      return response;
    } catch (error: any) {
      console.error("Deactivate Obligation Error:", error);

      const message = getErrorMessage(
        error,
        i18n.t("obligations:sliceMessages.deactivateError", {
          defaultValue: "Failed to deactivate obligation",
        }),
      );

      dispatch(setObligationError(message));

      toast.error(
        getErrorMessage(
          error,
          i18n.t("obligations:sliceMessages.deactivateError", {
            defaultValue: "Failed to deactivate obligation",
          }),
        ),
      );

      throw error;
    }
  };
};

export const searchObgContract = (payload: any = {}) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setObligationContractLoading(true));
    dispatch(clearObligationError());

    try {
      const response = await obligationsApi.searchObligationsContract(payload);

      const data = getResponseData(response);

      const contracts =
        data?.data ||
        data?.items ||
        data?.results ||
        (Array.isArray(data) ? data : []);

      dispatch(setObligationContracts(contracts));

      return response;
    } catch (error: any) {
      console.error("Search Obligation Contracts Error:", error);

      const message = getErrorMessage(
        error,
        i18n.t("obligations:sliceMessages.searchContractError", {
          defaultValue: "Failed to fetch obligation contracts",
        }),
      );

      dispatch(setObligationError(message));

      toast.error(
        i18n.t("obligations:sliceMessages.searchContractError", {
          defaultValue: "Failed to fetch obligation contracts",
        }),
      );

      throw error;
    } finally {
      dispatch(setObligationContractLoading(false));
    }
  };
};

export const searchObligationsContract = searchObgContract;

export const fetchObligationContracts = (
  payload: ObligationsSearchPayload = {},
) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setObligationContractLoading(true));
    dispatch(clearObligationError());

    try {
      const currentUser = await getCurrentUser();

      const requestBody = {
        pageIndex: 0,
        pageSize: 100,
        orderBy: "",
        sortAscending: false,
        sortDecending: false,
        includeFacets: false,
        ...payload,
        ...(currentUser?.id
          ? {
              userId: currentUser.id,
            }
          : {}),
      };

      const response =
        await obligationsApi.searchObligationsContract(requestBody);
      const data = getResponseData(response);

      const contracts =
        data?.data ||
        data?.items ||
        data?.results ||
        (Array.isArray(data) ? data : []);

      dispatch(setObligationContracts(contracts));

      return response;
    } catch (error: any) {
      console.error("Fetch Obligation Contracts Error:", error);

      const message = getErrorMessage(
        error,
        i18n.t("obligations:sliceMessages.searchContractError", {
          defaultValue: "Failed to fetch obligation contracts",
        }),
      );

      dispatch(setObligationError(message));

      toast.error(
        i18n.t("obligations:sliceMessages.searchContractError", {
          defaultValue: "Failed to fetch obligation contracts",
        }),
      );

      throw error;
    } finally {
      dispatch(setObligationContractLoading(false));
    }
  };
};

export const createObgContract = (formData: any) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setCreatingObligationContract(true));
    dispatch(clearObligationError());

    try {
      const response = await obligationsApi.createObligationContract(formData);

      const createdContract =
        getResponseData(response)?.data ||
        getResponseData(response) ||
        formData;

      const currentContracts = getState().obligations.obligationContracts || [];

      const contractId = createdContract?.id;

      const alreadyExists =
        contractId &&
        currentContracts.some((item: any) => item?.id === contractId);

      if (!alreadyExists) {
        dispatch(
          setObligationContracts([createdContract, ...currentContracts]),
        );
      }

      toast.success(
        i18n.t("obligations:sliceMessages.createContractSuccess", {
          defaultValue: "Obligation contract created successfully!",
        }),
      );

      return createdContract;
    } catch (error: any) {
      console.error("Create Obligation Contract Error:", error);

      const message = getErrorMessage(
        error,
        i18n.t("obligations:sliceMessages.createContractError", {
          defaultValue: "Failed to create obligation contract",
        }),
      );

      dispatch(setObligationError(message));

      toast.error(
        i18n.t("obligations:sliceMessages.createContractError", {
          defaultValue: "Failed to create obligation contract",
        }),
      );

      throw error;
    } finally {
      dispatch(setCreatingObligationContract(false));
    }
  };
};

const obligationsSlice = createSlice({
  name: "obligations",

  initialState,

  reducers: {
    setObligations: (state, action: PayloadAction<Obligation[]>) => {
      state.items = action.payload;
    },

    appendObligations: (state, action: PayloadAction<Obligation[]>) => {
      const existingIds = new Set(
        state.items.map((item) => item.id).filter(Boolean),
      );

      state.items = [
        ...state.items,
        ...action.payload.filter(
          (item) => !item.id || !existingIds.has(item.id),
        ),
      ];
    },

    setObligationPageIndex: (state, action: PayloadAction<number>) => {
      state.pageIndex = action.payload;
    },

    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },

    setTotalObligations: (state, action: PayloadAction<number>) => {
      state.totalObligations = action.payload;
    },

    setSelectedObligation: (
      state,
      action: PayloadAction<Obligation | null>,
    ) => {
      state.selectedObligation = action.payload;
    },

    addObligationToList: (state, action: PayloadAction<Obligation>) => {
      state.items = [action.payload, ...state.items];
    },

    updateObligationInList: (state, action: PayloadAction<Obligation>) => {
      const updated = action.payload;

      if (!updated?.id) {
        return;
      }

      const index = state.items.findIndex((item) => item.id === updated.id);

      if (index !== -1) {
        state.items[index] = {
          ...state.items[index],
          ...updated,
        };
      } else {
        state.items = [updated, ...state.items];
      }

      if (state.selectedObligation?.id === updated.id) {
        state.selectedObligation = {
          ...state.selectedObligation,
          ...updated,
        };
      }
    },

    removeObligationFromList: (state, action: PayloadAction<string>) => {
      const id = action.payload;

      state.items = state.items.filter((item) => item.id !== id);

      if (state.selectedObligation?.id === id) {
        state.selectedObligation = null;
      }
    },

    updateObligationStatus: (
      state,
      action: PayloadAction<{
        id: string;
        isActive: boolean;
      }>,
    ) => {
      const { id, isActive } = action.payload;

      const item = state.items.find((obligation) => obligation.id === id);

      if (item) {
        item.isActive = isActive;
      }

      if (state.selectedObligation?.id === id) {
        state.selectedObligation.isActive = isActive;
      }
    },

    setObligationLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setCreatingObligation: (state, action: PayloadAction<boolean>) => {
      state.isCreating = action.payload;
    },

    setUpdatingObligation: (state, action: PayloadAction<boolean>) => {
      state.isUpdating = action.payload;
    },

    setDeletingObligation: (state, action: PayloadAction<boolean>) => {
      state.isDeleting = action.payload;
    },

    setObligationError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    clearObligationError: (state) => {
      state.error = null;
    },

    setObligationContracts: (state, action: PayloadAction<any[]>) => {
      state.obligationContracts = action.payload;
    },

    clearObligationContracts: (state) => {
      state.obligationContracts = [];
    },

    setObligationContractLoading: (state, action: PayloadAction<boolean>) => {
      state.isObligationContractLoading = action.payload;
    },

    setCreatingObligationContract: (state, action: PayloadAction<boolean>) => {
      state.isCreatingObligationContract = action.payload;
    },
  },
});

export const {
  setObligations,
  appendObligations,
  setObligationPageIndex,
  setHasMore,
  setTotalObligations,
  setSelectedObligation,

  addObligationToList,
  updateObligationInList,
  removeObligationFromList,
  updateObligationStatus,

  setObligationLoading,
  setCreatingObligation,
  setUpdatingObligation,
  setDeletingObligation,

  setObligationError,
  clearObligationError,

  setObligationContracts,
  clearObligationContracts,
  setObligationContractLoading,
  setCreatingObligationContract,
} = obligationsSlice.actions;

export const selectObligations = (state: RootState) => state.obligations.items;

export const selectObligationPageIndex = (state: RootState) =>
  state.obligations.pageIndex;

export const selectObligationHasMore = (state: RootState) =>
  state.obligations.hasMore;

export const selectTotalObligations = (state: RootState) =>
  state.obligations.totalObligations;

export const selectSelectedObligation = (state: RootState) =>
  state.obligations.selectedObligation;

export const selectObligationLoading = (state: RootState) =>
  state.obligations.isLoading;

export const selectCreatingObligation = (state: RootState) =>
  state.obligations.isCreating;

export const selectUpdatingObligation = (state: RootState) =>
  state.obligations.isUpdating;

export const selectDeletingObligation = (state: RootState) =>
  state.obligations.isDeleting;

export const selectObligationError = (state: RootState) =>
  state.obligations.error;

export const selectObligationContracts = (state: RootState) =>
  state.obligations.obligationContracts;

export const selectObligationContractLoading = (state: RootState) =>
  state.obligations.isObligationContractLoading;

export const selectCreatingObligationContract = (state: RootState) =>
  state.obligations.isCreatingObligationContract;

export default obligationsSlice.reducer;
